import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="reporting-data"
export default class extends Controller {
  static targets = [
    "timeframeSelect",
    "eventSelect",
    "scopeSelect",
    "customDateRange",
    "startDateInput",
    "endDateInput",
    "totalEnrollments",
    "packagesList",
    "revenueCollected",
    "paymentPlansList",
    "recentEnrollmentsList",
    "collectedRevenueByPaymentPlan",
    "entityCreationCollected",
    "entityCreationOutstanding",
    "entityCreationProgressBar",
    "entityCreationProgressLabel",
    "entityCreationByPaymentPlanList",
    "revenueByChargeTypeList",
  ];

  connect() {
    this.toggleCustomDateRange();
    this.loadEvents();
    this.loadStats();
  }

  async changeTimeframe() {
    this.toggleCustomDateRange();
    this.eventSelectTarget.value = "";
    await this.loadEvents();
    this.loadStats();
  }

  changeCustomDate() {
    this.loadEvents();
    this.loadStats();
  }

  changeEvent() {
    this.loadStats();
  }

  toggleCustomDateRange() {
    const isCustom = this.timeframeSelectTarget.value === "custom";
    this.customDateRangeTarget.classList.toggle("hidden", !isCustom);
  }

  get customDateParams() {
    if (this.timeframeSelectTarget.value !== "custom") return "";

    return `&start_date=${this.startDateInputTarget.value}&end_date=${this.endDateInputTarget.value}`;
  }

  get currentScope() {
    if (this.hasScopeSelectTarget) {
      return this.scopeSelectTarget.value || "all";
    }

    return "all";
  }

  changeScope() {
    this.eventSelectTarget.value = "";
    this.loadEvents();
    this.loadStats();
  }

  async loadStats() {
    const timeframe = this.timeframeSelectTarget.value || "ytd";
    const eventId = this.eventSelectTarget.value || "";
    const scope = this.currentScope;

    let url = `/admin/events/reporting_data.json?timeframe=${timeframe}&scope=${scope}${this.customDateParams}`;
    if (eventId) {
      url += `&event_id=${eventId}`;
    }

    const response = await fetch(url);

    const data = await response.json();
    this.totalEnrollmentsTarget.textContent = data.total_enrollments; // Total number of enrollments

    this.recentEnrollmentsListTarget.innerHTML = data.recent_enrollments.length
      ? data.recent_enrollments
          .map(
            (enrollment) => `
        <div class="flex items-center justify-between gap-2">
          <span class="truncate">${enrollment.full_name} - ${enrollment.package}</span>
        </div>
      `,
          )
          .join("")
      : `<p class="text-xs text-indigo-400">No enrollments today</p>`; // Recent enrollments (today)

    this.packagesListTarget.innerHTML = data.enrollments_by_package
      .map(
        ({ id, name, count }) => `
        <div
          class="flex items-center justify-between gap-2 -mx-2 px-2 py-1 rounded cursor-pointer hover:bg-green-100 transition-colors"
          data-action="click->reporting-data#selectPackage"
          data-package-id="${id}"
        >
          <span>${name}</span>
          <span class="text-xl font-semibold text-green-800">${count}</span>
        </div>
      `,
      )
      .join(""); // Enrollments by package

    this.paymentPlansListTarget.innerHTML = data.enrollments_by_payment_plan
      .map(
        ({ name, value, count }) => `
        <div
          class="flex items-center justify-between gap-2 -mx-2 px-2 py-1 rounded cursor-pointer hover:bg-orange-100 transition-colors"
          data-action="click->reporting-data#selectPaymentPlan"
          data-payment-plan-value="${value}"
        >
          <span>${name}</span>
          <span class="text-xl font-semibold text-orange-800">${count}</span>
        </div>
      `,
      )
      .join(""); // Enrollments by payment plan

    this.collectedRevenueByPaymentPlanTarget.innerHTML = Object.entries(
      data.revenue_by_payment_plan,
    )
      .map(
        ([name, amount]) => `
        <div class="flex items-center justify-between">
          <span>${name}</span>
          <span class="text-xl font-semibold text-blue-800">${this.formatMoney(amount)}</span>
        </div>
      `,
      )
      .join(""); // Collected revenue by payment plan

    this.revenueCollectedTarget.textContent = this.formatMoney(
      data.revenue_collected,
    );

    this.entityCreationCollectedTarget.textContent = this.formatMoney(
      data.entity_creation_collected,
    );
    this.entityCreationOutstandingTarget.textContent = this.formatMoney(
      data.entity_creation_outstanding,
    );

    const collectedPct = this.percentCollected(
      data.entity_creation_collected,
      data.entity_creation_outstanding,
    );
    this.entityCreationProgressBarTarget.style.width = `${collectedPct}%`;
    this.entityCreationProgressLabelTarget.textContent = `${collectedPct}% collected`;

    this.entityCreationByPaymentPlanListTarget.innerHTML =
      data.entity_creation_by_payment_plan
        .map(({ name, collected, outstanding }) => {
          const planPct = this.percentCollected(collected, outstanding);

          return `
        <div class="space-y-1">
          <div class="flex items-center justify-between gap-2 text-sm">
            <span class="font-medium text-gray-800">${name}</span>
            <span class="text-xs text-gray-500 shrink-0">${this.formatMoney(collected)} / ${this.formatMoney(outstanding)} remaining</span>
          </div>
          <div class="w-full bg-amber-200 rounded-full h-1.5 overflow-hidden">
            <div class="bg-teal-600 h-1.5" style="width: ${planPct}%"></div>
          </div>
        </div>
      `;
        })
        .join(""); // Entity creation collected/outstanding by payment plan

    this.revenueByChargeTypeListTarget.innerHTML = data.revenue_by_charge_type
      .map(
        ({ name, amount }) => `
        <div class="flex items-center justify-between gap-2">
          <span>${name}</span>
          <span class="text-xl font-semibold text-purple-800">${this.formatMoney(amount)}</span>
        </div>
      `,
      )
      .join(""); // Revenue split by (initial payment,payment plan subscription or renewal subscription)

    this.notifyFiltersChanged(timeframe, eventId, scope);
  }

  notifyFiltersChanged(timeframe, eventId, scope) {
    const detail = { timeframe, scope };
    if (eventId) detail.event_id = eventId;
    if (timeframe === "custom") {
      detail.start_date = this.startDateInputTarget.value;
      detail.end_date = this.endDateInputTarget.value;
    }

    window.dispatchEvent(
      new CustomEvent("reporting-filters:changed", { detail }),
    );
  }

  selectPackage(event) {
    const { packageId } = event.currentTarget.dataset;
    if (!packageId) return;

    const modal = document.querySelector(
      '[data-id="enrollments_by_package_modal"]',
    );
    if (!modal) return;

    this.applyPackageFilter(modal, packageId);

    const modalController =
      this.application.getControllerForElementAndIdentifier(modal, "modal");
    modalController.open(event);
  }

  applyPackageFilter(modal, packageId) {
    const select = modal.querySelector(
      'select.filter[data-filter-key="package"]',
    );
    if (!select) return;

    if (select.tomselect) {
      select.tomselect.setValue(packageId);
    } else {
      select.value = packageId;
      select.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  selectPaymentPlan(event) {
    const { paymentPlanValue } = event.currentTarget.dataset;
    if (!paymentPlanValue) return;

    const modal = document.querySelector(
      '[data-id="enrollments_by_payment_plan"]',
    );
    if (!modal) return;

    this.applyPaymentPlanFilter(modal, paymentPlanValue);

    const modalController =
      this.application.getControllerForElementAndIdentifier(modal, "modal");
    modalController.open(event);
  }

  applyPaymentPlanFilter(modal, paymentPlanValue) {
    const select = modal.querySelector(
      'select.filter[data-filter-key="payment_plan"]',
    );
    if (!select) return;

    if (select.tomselect) {
      select.tomselect.setValue(paymentPlanValue);
    } else {
      select.value = paymentPlanValue;
      select.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  formatMoney(amount) {
    return `$${Number(amount).toLocaleString()}`;
  }

  percentCollected(collected, outstanding) {
    const total = Number(collected) + Number(outstanding);
    return total > 0 ? Math.round((Number(collected) / total) * 100) : 100;
  }

  async loadEvents() {
    const timeframe = this.timeframeSelectTarget.value || "ytd";
    const scope = this.currentScope;

    const response = await fetch(
      `/admin/events/reporting_events.json?timeframe=${timeframe}&scope=${scope}${this.customDateParams}`,
    );

    const events = await response.json();

    this.eventSelectTarget.innerHTML = `
      <option value="">All Events</option>
      ${events.map((event) => `<option value="${event.id}">${event.name}</option>`).join("")}
    `;
  }
}
